import React, { useMemo, useState } from 'react';
import { Bars3Icon, XMarkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export const VA_ACCOUNT_FORMAT_FIELDS = [
  { id: 'username', label: 'Username' },
  { id: 'password', label: 'Password' },
  { id: 'accessToken', label: 'AUTH' },
  { id: 'ct0', label: 'ct0 (CSRF)' },
  { id: 'proxyString', label: 'Proxy (IP:PORT:USERNAME:PASSWORD[:proxy_auth])' },
  { id: 'email', label: 'Email' },
  { id: 'emailPassword', label: 'Email password' },
  { id: 'displayName', label: 'Display Name' },
  { id: 'phone', label: 'Phone' },
  { id: 'sessionData', label: 'Session Data' },
  { id: 'unknown', label: 'Unknown' },
  { id: 'userAgent', label: 'User Agent' },
  { id: 'group', label: 'Group' },
  { id: 'tags', label: 'Tags' }
];

export const DEFAULT_VA_FORMAT_FIELDS = [
  { id: 'username', label: 'Username', enabled: true },
  { id: 'password', label: 'Password', enabled: true },
  { id: 'accessToken', label: 'AUTH', enabled: true },
  { id: 'proxyString', label: 'Proxy (IP:PORT:USERNAME:PASSWORD[:proxy_auth])', enabled: true }
];

const EXAMPLE_VALUES = {
  username: 'example_user',
  password: 'password123',
  phone: '+1234567890',
  email: 'user@example.com',
  emailPassword: 'email_pass123',
  accessToken: 'auth_token_here',
  proxyString: '192.168.1.1:8080:user:pass:proxy_auth'
};

const CustomFormatBuilder = ({
  availableFields = VA_ACCOUNT_FORMAT_FIELDS,
  fields,
  onFieldsChange,
  delimiter,
  onDelimiterChange,
  accountText,
  onAccountTextChange,
  textareaRows = 10
}) => {
  const [draggedField, setDraggedField] = useState(null);

  const getFormatPreview = () => {
    const enabledFields = fields.filter((f) => f.enabled);
    if (enabledFields.length === 0) return 'No fields enabled';
    return enabledFields.map((f) => EXAMPLE_VALUES[f.id] || f.label).join(` ${delimiter} `);
  };

  const accountCount = useMemo(
    () => accountText.split('\n').map((l) => l.trim()).filter(Boolean).length,
    [accountText]
  );

  const handleDragStart = (e, fieldId) => {
    setDraggedField(fieldId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedField === null) return;
    const draggedIndex = fields.findIndex((f) => f.id === draggedField);
    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDraggedField(null);
      return;
    }
    const next = [...fields];
    const [removed] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, removed);
    onFieldsChange(next);
    setDraggedField(null);
  };

  const toggleFieldEnabled = (fieldId) => {
    onFieldsChange(fields.map((f) => (f.id === fieldId ? { ...f, enabled: !f.enabled } : f)));
  };

  const addFieldToFormat = (fieldId) => {
    const field = availableFields.find((f) => f.id === fieldId);
    if (!field || fields.find((f) => f.id === fieldId)) return;
    onFieldsChange([...fields, { ...field, enabled: true }]);
  };

  const removeFieldFromFormat = (fieldId) => {
    onFieldsChange(fields.filter((f) => f.id !== fieldId));
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Custom Format Builder
          </label>
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-500 dark:text-gray-400">Delimiter:</label>
            <select
              value={delimiter}
              onChange={(e) => onDelimiterChange(e.target.value)}
              className="form-select text-sm py-1 px-2"
            >
              <option value=":">:</option>
              <option value="|">|</option>
              <option value=",">,</option>
              <option value=";">;</option>
              <option value="\t">Tab</option>
            </select>
          </div>
        </div>

        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Drag to reorder fields (click checkbox to toggle visibility)
            </p>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  addFieldToFormat(e.target.value);
                  e.target.value = '';
                }
              }}
              className="form-select text-xs py-1 px-2"
              defaultValue=""
            >
              <option value="">+ Add Field</option>
              {availableFields
                .filter((f) => !fields.find((cf) => cf.id === f.id))
                .map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.label}
                  </option>
                ))}
            </select>
          </div>

          <div className="space-y-2 min-h-[120px]">
            {fields.map((field, index) => (
              <div
                key={field.id}
                draggable
                onDragStart={(e) => handleDragStart(e, field.id)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`flex items-center gap-2 p-2 rounded border-2 transition-all cursor-move ${
                  draggedField === field.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 opacity-50'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-500'
                }`}
              >
                <Bars3Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={field.enabled}
                    onChange={() => toggleFieldEnabled(field.id)}
                    className="form-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={`text-sm flex-1 ${field.enabled ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500 line-through'}`}>
                    {field.label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFieldFromFormat(field.id);
                  }}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
            {fields.length === 0 && (
              <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
                No fields added. Click &quot;Add Field&quot; to get started.
              </div>
            )}
          </div>

          <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Format Preview:</span>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(getFormatPreview());
                  toast.success('Format preview copied to clipboard');
                }}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
              >
                <ClipboardDocumentIcon className="h-3 w-3" />
                Copy
              </button>
            </div>
            <code className="text-xs text-gray-800 dark:text-gray-200 break-all">
              {getFormatPreview()}
            </code>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Proxy supports optional <span className="font-mono">proxy_auth</span>:{' '}
              <span className="font-mono">host:port:user:pass:proxy_auth</span>
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Account Strings (one per line)
            </label>
            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
              {accountCount} account{accountCount !== 1 ? 's' : ''}
            </span>
          </div>
          <textarea
            value={accountText}
            onChange={(e) => onAccountTextChange(e.target.value)}
            rows={textareaRows}
            className="form-textarea w-full font-mono text-sm"
            placeholder={`Enter accounts in the format shown above, one per line.\nExample:\n${getFormatPreview()}`}
          />
          <p className="text-sm text-gray-500 mt-1">
            Supports username + password with phone or email, auth token, and proxy with optional proxy_auth. All fields are stored for future use.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomFormatBuilder;
